import {
  Box,
  Button,
  Flex,
  HStack,
  Image,
  Spinner,
  Stack,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { applyActionCode } from 'firebase/auth';
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { auth } from '../../firebase';
import { HelivanText } from '../helivan/HelivanText';
import { HelivanColor } from '../helivan/HelivanColor';
import { IoMailOutline, IoCheckmarkCircleOutline, IoRefreshOutline, IoLogOutOutline } from 'react-icons/io5';
import { MdOutlineSmartToy } from 'react-icons/md';
import { useGetPlatformStatsQuery } from '../../generated/graphql';

// Create a storage key for tracking processed verification codes
const PROCESSED_CODES_KEY = 'processedVerificationCodes';

// Get list of already processed verification codes
const getProcessedCodes = (): string[] => {
  try {
    const stored = localStorage.getItem(PROCESSED_CODES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (e) {
    console.error('Error parsing processed codes:', e);
    return [];
  }
};

// Add a code to the processed list
const markCodeAsProcessed = (code: string) => {
  try {
    const codes = getProcessedCodes();
    if (!codes.includes(code)) {
      codes.push(code);
      localStorage.setItem(PROCESSED_CODES_KEY, JSON.stringify(codes));
    }
  } catch (e) {
    console.error('Error storing processed code:', e);
  }
};

// Check if a code has already been processed
const hasCodeBeenProcessed = (code: string): boolean => {
  return getProcessedCodes().includes(code);
};

const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(num >= 10000 ? 0 : 1)}K`;
  }
  return num.toString();
};

const EmailVerification: React.FC = () => {
  const { data: statsData } = useGetPlatformStatsQuery();
  const {
    currentUser,
    isEmailVerified,
    isAuthenticated,
    loading: authLoading,
    sendVerificationEmail,
    checkEmailVerified,
    logout,
    setLoading,
  } = useAuth();

  const [localLoading, setLocalLoading] = useState(false);
  const [verificationInProgress, setVerificationInProgress] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(
    null
  );
  const toast = useToast();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const oobCode = queryParams.get('oobCode');
  const mode = queryParams.get('mode');
  const navigate = useNavigate();

  // Determine if this is a public verification request (has oobCode)
  const isPublicVerification = oobCode && mode === 'verifyEmail';

  // Reset global loading state when component mounts
  useEffect(() => {
    setLoading(false);
    return () => setLoading(false);
  }, [setLoading]);

  // Handle the oobCode verification when the component mounts
  // This can work without authentication (Firebase applyActionCode doesn't require auth)
  useEffect(() => {
    // Skip if there's no code, not in verification mode, or already processed/in progress
    if (!oobCode || mode !== 'verifyEmail' || verificationInProgress) {
      return;
    }

    // Check if we've already processed this code
    if (hasCodeBeenProcessed(oobCode)) {
      // If authenticated, check if we're already verified
      if (isAuthenticated && currentUser) {
        checkEmailVerified().then((isVerified) => {
          if (isVerified) {
            setVerificationSuccess(true);
          }
        });
      } else {
        // Code was processed, redirect to login with success flag
        sessionStorage.setItem('emailJustVerified', 'true');
        navigate('/login', { replace: true });
      }
      return;
    }

    // Mark the verification as in progress to prevent duplicate processing
    setVerificationInProgress(true);

    // Define the verification function
    const verifyEmailWithCode = async () => {
      try {
        // Apply the action code to verify the email
        // This works without authentication - Firebase processes the code server-side
        await applyActionCode(auth, oobCode);

        // Mark this code as processed to prevent future attempts
        markCodeAsProcessed(oobCode);

        // Store success flag for post-login redirect
        sessionStorage.setItem('emailJustVerified', 'true');

        // Add a delay to ensure Firebase has time to process the verification
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // If we have a current user, reload them to refresh their verification status
        if (isAuthenticated && currentUser) {
          await currentUser.reload();

          // Check if the email is now verified
          const isVerified = await checkEmailVerified();

          if (isVerified) {
            setVerificationSuccess(true);

            // Show success toast
            toast({
              title: 'Email verified!',
              description: 'Your email has been successfully verified.',
              status: 'success',
              duration: 3000,
              isClosable: true,
            });

            // Clear the just verified flag since we're already authenticated
            sessionStorage.removeItem('emailJustVerified');
          } else {
            setVerificationError(
              'Verification completed, but email status not updated yet. Please try refreshing the page.'
            );
            setVerificationInProgress(false);
          }
        } else {
          // Not authenticated - show success and redirect to login
          toast({
            title: 'Email verified!',
            description: 'Please sign in to continue.',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });

          // Redirect to login - the emailJustVerified flag will help with post-login redirect
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 1500);
        }
      } catch (error: any) {
        console.error('Error verifying email:', error);

        // Don't show errors for already processed codes
        if (
          error?.code === 'auth/invalid-action-code' &&
          hasCodeBeenProcessed(oobCode)
        ) {
          // This is expected if the code was already used successfully
          if (isAuthenticated && currentUser) {
            const isVerified = await checkEmailVerified();
            if (isVerified) {
              setVerificationSuccess(true);
              return;
            }
          } else {
            // Redirect to login
            navigate('/login', { replace: true });
            return;
          }
        }

        setVerificationError(error.message || 'Verification failed');
        setVerificationInProgress(false);

        toast({
          title: 'Verification failed',
          description:
            error.message ||
            'Please try again or request a new verification link',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    };

    // Execute the verification
    verifyEmailWithCode();
  }, [
    oobCode,
    mode,
    currentUser,
    isAuthenticated,
    navigate,
    toast,
    checkEmailVerified,
    setLoading,
    verificationInProgress,
  ]);

  // Function to manually send verification email
  const handleResendEmail = async () => {
    if (localLoading || !currentUser) return;

    setLocalLoading(true);
    try {
      await sendVerificationEmail();

      toast({
        title: 'Verification email sent',
        description: 'Please check your inbox and spam folder',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error('Error sending verification email:', error);
      toast({
        title: 'Failed to send verification email',
        description: error.message || 'Please try again',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLocalLoading(false);
    }
  };

  // Function to handle logout
  const handleLogout = async () => {
    try {
      await logout();
      // Redirect will happen automatically due to auth state change
    } catch (error: any) {
      console.error('Error logging out:', error);
      toast({
        title: 'Failed to log out',
        description: error.message || 'Please try again',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // If this is a public verification request (has oobCode), show loading while processing
  if (isPublicVerification && verificationInProgress) {
    return (
      <Flex
        minH="100vh"
        w="full"
        justifyContent="center"
        alignItems="center"
        bg={HelivanColor.background.secondary}
      >
        <VStack spacing={4}>
          <Spinner size="xl" color={HelivanColor.text.helivanBlue} thickness="3px" />
          <HelivanText variant="sectionHeader" colorOverride={HelivanColor.text.blueDark}>
            Verifying your email...
          </HelivanText>
        </VStack>
      </Flex>
    );
  }

  // If not authenticated and no oobCode, redirect to login
  if (!authLoading && !isAuthenticated && !isPublicVerification) {
    return <Navigate to="/login" replace />;
  }

  // If already verified or verification was successful, navigate to projects
  if (isEmailVerified || verificationSuccess) {
    // Check for pending invite
    const pendingInviteToken = sessionStorage.getItem('pendingInviteToken');
    if (pendingInviteToken) {
      return <Navigate to={`/accept-invite?token=${pendingInviteToken}`} replace />;
    }
    return <Navigate to="/agents" replace />;
  }

  // Show loading while auth is loading
  if (authLoading) {
    return (
      <Flex
        minH="100vh"
        w="full"
        justifyContent="center"
        alignItems="center"
        bg={HelivanColor.background.secondary}
      >
        <Spinner size="xl" color={HelivanColor.text.helivanBlue} thickness="3px" />
      </Flex>
    );
  }

  const LoadingOverlay = () => (
    <Flex
      position="fixed"
      top="0"
      left="0"
      right="0"
      bottom="0"
      bg="rgba(255, 255, 255, 0.9)"
      zIndex="9999"
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      backdropFilter="blur(4px)"
    >
      <Spinner size="xl" color={HelivanColor.text.helivanBlue} thickness="3px" />
      <HelivanText variant="sectionHeader" colorOverride={HelivanColor.text.blueDark} mt={4}>
        Verifying your email...
      </HelivanText>
    </Flex>
  );

  return (
    <Flex
      minH="100vh"
      w="full"
      bg={HelivanColor.background.secondary}
    >
      {verificationInProgress && <LoadingOverlay />}

      {/* Left side - Branding */}
      <Flex
        flex="1"
        display={['none', 'none', 'none', 'flex']}
        bg="linear-gradient(135deg, #D8E8FF 0%, #E8F1FF 50%, #F0F6FF 100%)"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        position="relative"
        overflow="hidden"
      >
        {/* Decorative elements */}
        <Box
          position="absolute"
          top="-20%"
          right="-10%"
          width="400px"
          height="400px"
          borderRadius="full"
          bg="rgba(0,71,125,0.05)"
        />
        <Box
          position="absolute"
          bottom="-15%"
          left="-5%"
          width="300px"
          height="300px"
          borderRadius="full"
          bg="rgba(0,71,125,0.03)"
        />

        <VStack spacing={8} zIndex={1} px={12} textAlign="center">
          <HStack spacing={3}>
            <Image
              src="/img/agent-watch-icon.svg"
              alt="AgentWatch Logo"
              height="56px"
              width="56px"
              objectFit="contain"
            />
            <Text
              fontSize="36px"
              fontWeight={600}
              color={HelivanColor.text.blueDark}
            >
              AgentWatch
            </Text>
          </HStack>

          <VStack spacing={4} maxW="420px">
            <Text
              fontSize="24px"
              fontWeight={300}
              color={HelivanColor.text.blueDark}
              lineHeight="1.4"
            >
              Monitor your AI agents. Get notified when they change.
            </Text>
            <Text
              fontSize="15px"
              color={HelivanColor.text.blueMid}
              lineHeight="1.7"
            >
              AI agents can drift over time due to model updates, fine-tuning, or changing contexts. AgentWatch tracks your agents and alerts you when their behaviors shift—so you can maintain trust and reliability.
            </Text>
          </VStack>

          <HStack spacing={6} mt={4}>
            <VStack spacing={1}>
              <HStack spacing={2}>
                <MdOutlineSmartToy size={20} color={HelivanColor.text.helivanBlue} />
                <Text fontSize="28px" fontWeight={600} color={HelivanColor.text.blueDark}>
                  {statsData?.getPlatformStats?.totalAgents ?? '500+'}
                </Text>
              </HStack>
              <Text fontSize="13px" color={HelivanColor.text.blueMid}>Agents / Personas</Text>
            </VStack>
            <Box width="1px" height="40px" bg={HelivanColor.border.primary} />
            <VStack spacing={1}>
              <Text fontSize="28px" fontWeight={600} color={HelivanColor.text.blueDark}>Hourly</Text>
              <Text fontSize="13px" color={HelivanColor.text.blueMid}>Monitoring</Text>
            </VStack>
            <Box width="1px" height="40px" bg={HelivanColor.border.primary} />
            <VStack spacing={1}>
              <Text fontSize="28px" fontWeight={600} color={HelivanColor.text.blueDark}>
                {statsData?.getPlatformStats?.totalChangesDetected
                  ? formatNumber(statsData.getPlatformStats.totalChangesDetected)
                  : '0'}
              </Text>
              <Text fontSize="13px" color={HelivanColor.text.blueMid}>Changes Detected</Text>
            </VStack>
          </HStack>
        </VStack>
      </Flex>

      {/* Right side - Verification content */}
      <Flex
        flex={['1', '1', '1', '0 0 520px']}
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        px={[6, 8, 12]}
        py={8}
        bg="white"
      >
        {/* Mobile logo */}
        <HStack spacing={2} display={['flex', 'flex', 'flex', 'none']} mb={8}>
          <Image
            src="/img/agent-watch-icon.svg"
            alt="AgentWatch Logo"
            height="32px"
            width="32px"
            objectFit="contain"
          />
          <Text
            fontSize="24px"
            fontWeight={600}
            color={HelivanColor.text.blueDark}
          >
            AgentWatch
          </Text>
        </HStack>

        <Stack
          width="full"
          maxWidth="400px"
          spacing={8}
          alignItems="center"
        >
          {/* Email icon */}
          <Flex
            width="80px"
            height="80px"
            borderRadius="full"
            bg={HelivanColor.background.secondary}
            justifyContent="center"
            alignItems="center"
          >
            <IoMailOutline size={40} color={HelivanColor.text.helivanBlue} />
          </Flex>

          <VStack spacing={3} textAlign="center">
            <Text
              fontSize="28px"
              fontWeight={600}
              color={HelivanColor.text.blueDark}
            >
              {verificationError ? 'Verification Error' : 'Verify your email'}
            </Text>
            <Text
              fontSize="15px"
              color={HelivanColor.text.blueMid}
              lineHeight="1.6"
            >
              We've sent a verification link to{' '}
              <Text as="span" fontWeight={600} color={HelivanColor.text.blueDark}>
                {currentUser?.email}
              </Text>
              . Click the link in your email to complete your registration.
            </Text>
          </VStack>

          {verificationError && (
            <Box
              p={4}
              bg="red.50"
              borderRadius="12px"
              border="1px solid"
              borderColor="red.200"
              width="full"
            >
              <Text fontSize="14px" color="red.600" textAlign="center">
                {verificationError}
              </Text>
            </Box>
          )}

          {/* Checklist */}
          <VStack
            spacing={3}
            width="full"
            p={4}
            bg={HelivanColor.background.secondary}
            borderRadius="12px"
          >
            <Text fontSize="14px" fontWeight={500} color={HelivanColor.text.blueDark}>
              Didn't receive the email?
            </Text>
            <VStack spacing={2} alignItems="flex-start" width="full">
              <HStack spacing={2}>
                <IoCheckmarkCircleOutline size={16} color={HelivanColor.text.blueMid} />
                <Text fontSize="13px" color={HelivanColor.text.blueMid}>
                  Check your spam or junk folder
                </Text>
              </HStack>
              <HStack spacing={2}>
                <IoCheckmarkCircleOutline size={16} color={HelivanColor.text.blueMid} />
                <Text fontSize="13px" color={HelivanColor.text.blueMid}>
                  Make sure your email address is correct
                </Text>
              </HStack>
              <HStack spacing={2}>
                <IoCheckmarkCircleOutline size={16} color={HelivanColor.text.blueMid} />
                <Text fontSize="13px" color={HelivanColor.text.blueMid}>
                  Wait a few minutes and check again
                </Text>
              </HStack>
            </VStack>
          </VStack>

          {/* Action buttons */}
          <VStack spacing={3} width="full">
            <Button
              onClick={handleResendEmail}
              isLoading={localLoading}
              loadingText="Sending..."
              height="48px"
              width="full"
              bg={HelivanColor.text.helivanBlue}
              color="white"
              borderRadius="10px"
              fontSize="15px"
              fontWeight={500}
              _hover={{ bg: HelivanColor.text.blueDark }}
              transition="all 0.2s"
              leftIcon={<IoRefreshOutline size={18} />}
            >
              Resend verification email
            </Button>

            <Button
              onClick={handleLogout}
              height="48px"
              width="full"
              bg="white"
              color={HelivanColor.text.blueMid}
              border="1px solid"
              borderColor={HelivanColor.border.primary}
              borderRadius="10px"
              fontSize="15px"
              fontWeight={500}
              _hover={{ bg: HelivanColor.background.secondary, borderColor: HelivanColor.text.blueMid }}
              transition="all 0.2s"
              leftIcon={<IoLogOutOutline size={18} />}
            >
              Sign out
            </Button>
          </VStack>

          <Text fontSize="13px" color={HelivanColor.text.gray} textAlign="center">
            Need help?{' '}
            <Text
              as="a"
              href="mailto:support@helivan.co"
              color={HelivanColor.text.helivanBlue}
              fontWeight={500}
              _hover={{ textDecoration: 'underline' }}
            >
              Contact support
            </Text>
          </Text>
        </Stack>
      </Flex>
    </Flex>
  );
};

export default EmailVerification;
